/* @flow */
import URI from 'urijs';
import { getAvatarUrlFromKey } from 'lib/Avatars';
import API from 'lib/RemindAPI';
import Avatar from 'entities/Avatar';
import Entity from 'lib/Entity';
import GroupMemberships from 'collections/GroupMemberships';
import Organization from 'entities/Organization';
import { Roles } from 'user/UserConstants';
import { TARGET_TYPES } from 'entities/Message';

import type { Target } from 'entities/Message';

export const GROUP_MEMBER_PAGE_SIZE = 200;
export const DEFAULT_COLOR = '#2f75c1';

// https://github.com/remind101/r101-api/blob/master/app/api/v2/entities/medium.rb
export type MediumInfoType = {
  raw: string,
  uri: string,
  pretty: string,
};

// https://github.com/remind101/r101-api/blob/426f7353ce7d9e8834e5792c5372ab6af3f573d9/app/api/v2/entities/group.rb#L73-L86
export type SubscribeInfoType = {
  sms: ?MediumInfoType,
  sms_alternate?: ?MediumInfoType,
  email: MediumInfoType,
};

export type RoleInfoType = {
  role: string,
  count: number,
};

export type LoadGroupMembershipsParams = {|
  page?: number,
  perPage?: number,
  query?: string,
  role?: string,
  userIds?: number[],
|};

export default class Group extends Entity {
  initialize() {
    this.urlRoot = '/classes';
  }

  defaults() {
    return {
      class_name: '',
      group_avatar: null,
      has_children: false,
      memberships_count: 1,
      messages_count: 0,
      name: null,
      possessive_name: '',
      subscriber_initiated_chats: true,
      searchable: true,
      type: '',
      owners: [],
      organization: null,
      roles: [],
    };
  }

  parse(response: Object): Object {
    const parsed = Object.assign({}, response);

    if (response['group_avatar']) {
      parsed['group_avatar'] = Avatar.fromJSON(response['group_avatar']);
    }

    if (response['created_at']) {
      parsed['created_at'] = new Date(response['created_at']);
    }

    if (response['owners']) {
      parsed['owners'] = GroupMemberships.fromJSON(response['owners']);
    }

    if (response['organization']) {
      parsed['organization'] = Organization.fromJSON(response['organization']);
    }

    if (response['archived_at']) {
      parsed['archived_at'] = new Date(response['archived_at']);
    }

    return parsed;
  }

  url(): string {
    if (!this.id) {
      const code = this.getCode();
      if (code) {
        return `${this.urlRoot}/@${code}`;
      }
    }
    return super.url();
  }

  webUrl(): ?string {
    const code = this.getCode();
    if (code) {
      return `/classes/${code}`;
    }
    return null;
  }

  isRoot(): boolean {
    return false;
  }

  getUuid(): ?string {
    return this.get('uuid');
  }

  getCreatedAt(): Date {
    return this.get('created_at');
  }

  getName(): string {
    return this.get('class_name');
  }

  getOwners() {
    return this.get('owners');
  }

  // XXX should be immutable once created
  // TODO move into new Group arguments at caller
  setName(name: string) {
    this.set('class_name', name);
  }

  getCode(): ?string {
    return this.get('name');
  }

  getPdfUrl(locale: ?string) {
    if (locale) {
      return new URI(this.get('pdf_url')).addQuery('locale', locale).toString();
    } else {
      return this.get('pdf_url');
    }
  }

  getJoinUrl() {
    return this.get('join_url');
  }

  hasChildren() {
    // Property is nullable on server-side, so ensure this is boolean
    return !!this.get('has_children');
  }

  setHasChildren(hasChildren: boolean) {
    this.set('has_children', hasChildren);
  }

  hasStudentOrParentOwner(): boolean {
    return !!this.getOwners().some(p => {
      const role = p.getUser() ? p.getUser().getRole() : '';
      return role === Roles.parent || role === Roles.student;
    });
  }

  getMembershipsCount() {
    return this.get('memberships_count');
  }

  getMembershipCountText() {
    const memberCount = this.getMembershipsCount();
    return memberCount === 1 ? '1 person' : `${memberCount} people`;
  }

  getSubscriptionInfo(): SubscribeInfoType {
    return this.get('subscribe');
  }

  // This group may support short codes, but not if the current user is in Canada
  // $FlowFixMe
  useShortCodeForUser(user) {
    return this.getSubscriptionInfo().sms && user.getCountryCode() === 'US' && this.hasShortCode();
  }

  // This group may support SMS subscription, but not if the current user is in Afghanistan
  // $FlowFixMe
  smsEnabledForUser(user) {
    return this.getSubscriptionInfo().sms && ['US', 'CA'].includes(user.getCountryCode());
  }

  getType() {
    return this.get('type');
  }

  isStaffClass() {
    return this.getType() === 'organization';
  }

  getStaffClassOrgId(): ?number {
    return this.get('staff_class_org_id');
  }

  getOrganization() {
    return this.get('organization');
  }

  setOrganization(o: Organization) {
    this.set('organization', o);
  }

  getMessagesCount() {
    return this.get('messages_count');
  }

  isMessageHistoryEnabled() {
    return this.get('message_history_enabled');
  }

  getLastReadSeq(): ?number {
    return this.get('last_read_seq');
  }

  canSubscribersInitiateChats(): boolean {
    return this.get('subscriber_initiated_chats');
  }

  setCanSubscribersInititateChats(canSubscribersInitiateChats: boolean) {
    this.set('subscriber_initiated_chats', canSubscribersInitiateChats);
  }

  hasJoinCode() {
    return !this.isStaffClass() && this.getType() !== 'root';
  }

  getPermissions() {
    return this.get('permissions');
  }

  canJoin() {
    if (this.getPermissions()) {
      return this.getPermissions()['can_join'];
    }
    return true;
  }

  isOwnedByUser(userId: number) {
    return this.getOwners().map(o => o.getUserId()).includes(userId);
  }

  canEdit() {
    return this.getPermissions()['can_edit'];
  }

  canLeave() {
    return !this.getPermissions() || this.getPermissions()['can_leave'];
  }

  canAddOwner() {
    return this.canEdit() && !this.isStaffClass();
  }

  /**
   * @deprecated use postable roles instead
   */
  canPost() {
    return this.getPermissions()['can_post'];
  }

  canDelete() {
    return this.get('permissions')['can_delete'];
  }

  canAddPeople() {
    return this.getPermissions()['can_add_people'];
  }

  getPostableRoles() {
    return this.getPermissions().postable_roles || [];
  }

  isSMSEnabled() {
    // while debugging https://my.trackjs.com/messages/filtered?hash=1eb254c56674faa5
    // https://my.trackjs.com/messages/filtered?hash=43b3602fb3e91315
    // https://github.com/remind101/r101-frontend/commit/60715a0698224de39a5000dcd2b550e5e835ea74
    // Attributes are Group defaults, possibly because the Group entity is being accessed
    // before the group is loaded https://my.trackjs.com/shared/NzE3ZTI4NGY1NmJmNGQ1Y2E3OTA1ODNjNGVhNjAwZmQ
    // We recover with the catch, but what else is missing from the UI that we're not detecting?
    try {
      const { sms } = this.getSubscriptionInfo();
      return !!sms;
    } catch (e) {
      console.error('Group.isSMSEnabled() failed', this.attributes);
      return false;
    }
  }

  hasShortCode() {
    const { sms } = this.getSubscriptionInfo();
    return sms && (sms['raw'].length === 5);
  }

  getPossessiveName() {
    return this.get('possessive_name');
  }

  // @return string like "B. McGriff, B. McGriff, L. Feringa, and Mr Wu's "
  getPossessiveSignature(): string {
    // TODO XXX have API send this
    // For now, infer it from possessive_name that API does send like this:
    // https://github.com/remind101/r101-api/blob/426f7353ce7d9e8834e5792c5372ab6af3f573d9/app/decorators/group_decorator.rb#L18-L21
    const sigPlusName = this.getPossessiveName();
    const startOfName = sigPlusName.indexOf(this.getName());
    return sigPlusName.substr(0, startOfName - 1); // minus one for the space
  }

  getSMSNumber() {
    const { sms } = this.getSubscriptionInfo();
    if (sms) {
      return sms.pretty;
    }
    return null;
  }

  getSMSAlternateNumber() {
    const { sms_alternate: smsAlt } = this.getSubscriptionInfo();
    if (smsAlt) {
      return smsAlt.pretty;
    }

    return null;
  }

  getEmail() {
    return this.getSubscriptionInfo()['email']['pretty'];
  }

  isSearchable(): boolean {
    return this.get('searchable');
  }

  setSearchable(value: boolean) {
    return this.set('searchable', value);
  }

  getAvatarId(): ?number {
    const avatar = this.get('group_avatar');
    return avatar ? avatar.id : null;
  }

  getAvatar() {
    return this.get('group_avatar');
  }

  getAvatarUrl(): ?string {
    const avatar = this.getAvatar();
    return avatar ? avatar.getUrl() : getAvatarUrlFromKey(this.id);
  }

  getAccentColor(): string {
    const avatar = this.getAvatar();

    if (avatar && avatar.getColor()) {
      return `#${avatar.getColor()}`;
    }

    return DEFAULT_COLOR;
  }

  setAvatar(avatar: Avatar) {
    this.set('group_avatar', avatar);
  }

  setLastReadSeq(seq: number) {
    return this.set('last_read_seq', seq);
  }

  getRoles(): RoleInfoType[] {
    return this.get('roles');
  }

  // XXX .state is only set client side, should be in store instead of entity
  getState() {
    return this.get('state');
  }
  setState(state: Object) {
    return this.set({ state });
  }

  isAvailable(): boolean {
    return this.getState() === 'available';
  }

  isSubscribed(): boolean {
    return this.getState() === 'subscribed';
  }

  setThreadPreview(role: string, preview: string, sentAt: Date): void {
    const thread = this.get('threads').find(t => t.role === role);
    thread.preview = {
      preview,
      sent_at: sentAt,
    };
  }

  getThreads(): Target[] {
    let result = [];
    const targets = this.get('threads');
    if (targets) {
      result = targets.map((t) => {
        return { type: TARGET_TYPES.GROUP_TARGET, ...t };
      });
    }
    return result;
  }

  getPostableThreads(): Target[] {
    const postableRoles = this.getPostableRoles();
    return this.getThreads().filter(t => !!postableRoles.includes(t.role));
  }

  getThread(role: string): ?Target {
    return this.getThreads().find(t => t.role === role);
  }

  loadPotentialOwners(page: number = 1, perPage: number = GROUP_MEMBER_PAGE_SIZE,
    query: string = ''): Promise<GroupMemberships> {
    return API.request(`${this.url()}/potential_owners`, 'GET', {
      page,
      per_page: perPage,
      q: query,
    }).then(response => GroupMemberships.fromJSON(response.potential_owners));
  }

  loadGroupMemberships(params: LoadGroupMembershipsParams): Promise<GroupMemberships> {
    const parameters = {
      page: 1,
      perPage: GROUP_MEMBER_PAGE_SIZE,
      ...params,
    };

    const options = {
      per_page: parameters.perPage,
      user_ids: parameters.userIds,
      role: parameters.role,
      page: parameters.page,
      q: parameters.query,
    };

    const resourceId = this.id || this.getUuid();
    return API.request(`/classes/${resourceId}/members`, 'GET', options).then(
      response => GroupMemberships.fromJSON(response.members)
    );
  }

  promoteGroupMemberships(memberIds: number[]): Promise<GroupMemberships> {
    return API.request(`${this.url()}/members`, 'PATCH', {
      members: memberIds.map(id => {
        return {
          id,
          role: 'owner',
        };
      }),
    }).then(response => GroupMemberships.fromJSON(response.members));
  }

  resignOwnership(): Promise<Group> {
    return API.request(`/classes/${this.id}`, 'PATCH', {
      type: 'subscribed',
    }).then(group => Group.fromJSON(group));
  }

  // $FlowFixMe
  removeMember(relatedUser) {
    return API.request(`/classes/${this.id}/members/${relatedUser.id}`, 'DELETE', {});
  }

  create(): Promise<Group> {
    return API.request('/classes', 'POST', {
      group: {
        has_children: this.hasChildren(),
        class_name: this.getName(),
        name: this.getCode(),
        subscriber_initiated_chats: this.canSubscribersInitiateChats(),
        group_avatar_id: this.getAvatarId(),
        searchable: this.isSearchable(),
        organization_id: this.getOrganization() ? this.getOrganization().id : null,
      },
    }).then(group => Group.fromJSON(group));
  }

  patch(attributes: Object) {
    return this.save({}, {
      data: attributes,
      patch: true,
    });
  }

  // $FlowFixMe
  addSubscribers(subscribers): Promise<GroupMemberships> {
    return API.request(`/classes/${this.id}/members`, 'POST', {
      members: subscribers.map(subscriber => {
        return {
          id: subscriber.id,
        };
      }),
    }).then(response => GroupMemberships.fromJSON(response.members));
  }

  /**
   * Subscribes the current user to the group
   */
  subscribe(): Promise<Group> {
    return API.request(`${this.url()}/subscription`, 'POST').then(group => Group.fromJSON(group));
  }

  /**
   * Unsubscribes the current user from the group
   */
  unsubscribe(): Promise<*> {
    return API.request(`${this.url()}/subscription`, 'DELETE');
  }

  toJSON(): Object {
    return this.recursivelyToJSON();
  }

  archive(): Promise<Group> {
    return API.request(`/classes/${this.id}/archive`, 'POST').then(group => Group.fromJSON(group));
  }

  reuse(organizationId: ?number, groupCode?: string): Promise<Group> {
    return API.request(`/classes/${this.id}/reuse`, 'POST', {
      organization_id: organizationId,
      name: groupCode || null,
    }).then(group => Group.fromJSON(group));
  }

  unarchive(organizationId: ?number, groupCode?: string): Promise<Group> {
    return API.request(`/classes/${this.id}/unarchive`, 'POST', {
      organization_id: organizationId,
      name: groupCode || null,
    }).then(group => Group.fromJSON(group));
  }

  isArchived(): boolean {
    return !!this.get('archived_at');
  }

  getArchivedDate(): ?Date {
    return this.get('archived_at');
  }

  getOriginalOrganizationId(): number {
    return this.get('original_organization_id');
  }

  static parseAcceptableGroupCode(code: string): ?string {
    const matches = code.match(/[a-zA-Z0-9-]+/);
    return matches && matches.length === 1 ?
      matches[0] : null;
  }

  static createGroupFromCode(code: string): Group {
    return new Group({ name: code });
  }

  static fetchByCode(groupCode: string): Promise<Group> {
    // TODO: This should be replaced by /v2/classes/:code. We currently depend on /v2/groups/:code
    // to validate whether a group can be joined.
    return API.request(`/groups/@${groupCode}`, 'GET', {}).then(group => Group.fromJSON(group));
  }

  static fetchById(groupId: number): Promise<Group> {
    return API.request(`/classes/${groupId}`, 'GET', {}).then(group => Group.fromJSON(group));
  }

  static suggestCode(groupName: string): Promise<*> {
    return API.request('/groups/suggested_codes', 'GET', {
      'group[class_name]': groupName,
    });
  }

  static validateCodeUniqueness(groupCode: string): Promise<*> {
    return API.request('/groups/valid_code', 'GET', {
      'group[name]': groupCode,
    }).then((response) => {
      if (response.success) {
        return Promise.resolve(null);
      } else {
        return Promise.resolve(response);
      }
    });
  }
}

// Manually export this for ES5 compatibility
Group.GROUP_MEMBER_PAGE_SIZE = GROUP_MEMBER_PAGE_SIZE;



// WEBPACK FOOTER //
// ./src/entities/Group.js